import { type ContractConfig, type Market, MarketType } from '@/common/predictionMarkets/types.ts';
import { parseUnits } from '@/common/helpers.ts';
import { evmProvider } from '@/common/predictionMarkets/predictionMarkets.ts';
import { ref } from 'vue';

let configCache: ContractConfig | null = null;
export const isReady = ref(false);

export async function loadConfig() {
  // Get virtual liquidity multiplier from contract config
  const provider = await evmProvider.instance;
  configCache = await provider.getContractConfig();
  isReady.value = true;
}

function getConfigSync() {
  if (!configCache) {
    loadConfig();
    throw new Error('Config not loaded yet. Please make sure to call getConfig() on the provider before using calculateShares.');
  }
  return configCache;
}

export function calculateShares(market: Market, outcomeId: string, amountStr: string): bigint {
  if (market.marketType === MarketType.Deadline) return BigInt(0);
  if (market.marketType !== MarketType.Live) return BigInt(0);

  const outcomeIndex = Number(outcomeId);
  if (!market.outcomes[outcomeIndex] || !market.outcomes[outcomeIndex].probability) return BigInt(0);
  const amount = parseUnits(amountStr, 18);

  const config = getConfigSync();
  const virtualLiquidityTotal = BigInt(market.minPredictionAmount) * BigInt(config.virtualLiquidityMultiplier);

  // Solidity:
  const probBps = BigInt(market.outcomes[outcomeIndex].probability * 100);
  const virtualLiquidityOnOutcome = (virtualLiquidityTotal * probBps) / 10000n;

  const totalPerOutcome = market.outcomes.map((o) => parseUnits(o.volume.toString(), 18));

  const alreadyOnOutcome = totalPerOutcome[outcomeIndex] || 0n;
  const outcomeBefore = alreadyOnOutcome + virtualLiquidityOnOutcome;

  let totalBefore = virtualLiquidityTotal;
  for (const t of totalPerOutcome) {
    totalBefore += t;
  }

  const totalAfter = totalBefore + amount;
  const outcomeAfter = outcomeBefore + amount;

  if (outcomeAfter <= 0n) {
    throw new Error('SharesCalculationError');
  }

  // 1e18 precision like Solidity
  const PRECISION = 10n ** 18n;

  const oddsAfter = (totalAfter * PRECISION) / outcomeAfter;
  const oddsBefore = (totalBefore * PRECISION) / outcomeBefore;

  if (oddsAfter < PRECISION) {
    throw new Error('OddsTooLessThanOneError');
  }

  // lowestPossibleOdds from config is 1e4 precision
  // Multiply by 1e14 to reach 1e18 precision for comparison
  const lowestPossibleOdds = config.lowestPossibleOdds * 10n ** 14n;

  if (oddsAfter < lowestPossibleOdds) {
    throw new Error('OddsTooLowError');
  }

  const avgOdds = (oddsAfter + oddsBefore) / 2n;

  const shares = (amount * avgOdds) / PRECISION;

  if (shares <= 0n) {
    throw new Error('SharesCalculationError');
  }

  return shares;
}
